/**
 * Tracking utility — n8n webhook pipelines + PostHog
 *
 * Pipelines:
 *   General events  → /webhook/SlabV                (existing color comparison etc.)
 *   Liked visuals   → /webhook/visual-liked
 *   Downloads       → /webhook/visual-downloaded
 *   Session summary → /webhook/session-summary
 */

import posthog from 'posthog-js';

const BASE = 'https://elegantfittings.app.n8n.cloud';

const ENDPOINTS = {
  default:           `${BASE}/webhook/SlabV`,
  visual_liked:      `${BASE}/webhook/visual-liked`,
  visual_downloaded: `${BASE}/webhook/visual-downloaded`,
  session_summary:   `${BASE}/webhook/session-summary`,
};

// Route event name → correct pipeline endpoint
const getEndpoint = (eventName) =>
  ENDPOINTS[eventName] || ENDPOINTS.default;

// ─── Session ID ───────────────────────────────────────────────────────────────
const getSessionId = () => {
  let id = sessionStorage.getItem('tracking_session_id');
  if (!id) {
    id = 'sess_' + Math.random().toString(36).slice(2, 11) + '_' + Date.now();
    sessionStorage.setItem('tracking_session_id', id);
  }
  return id;
};

// ─── Session activity accumulator (for session_summary) ───────────────────────
const session = {
  startTime:          Date.now(),
  materialsViewed:    new Set(),
  likesCount:         0,
  downloadsCount:     0,
  comparisonsCount:   0,
  sampleRequested:    false,
  viewsVisited:       new Set(),
  cabinetColorsTried: new Set(),
  brandsExplored:     new Set(),
};

export const sessionActivity = session; // exposed so App.js can mutate it

// ─── Device ───────────────────────────────────────────────────────────────────
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) return 'mobile';
  return 'desktop';
};

// ─── Core send ────────────────────────────────────────────────────────────────
const send = async (url, payload) => {
  console.log(`[TRACKING] ${payload.event}:`, payload);
  try {
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (err) {
    console.debug('Tracking failed:', err);
  }
};

// ─── General event tracker ────────────────────────────────────────────────────
export const trackEvent = async (eventName, eventData = {}) => {
  const payload = {
    event:      eventName,
    timestamp:  new Date().toISOString(),
    session_id: getSessionId(),
    device:     getDeviceType(),
    url:        window.location.href,
    ...eventData,
  };

  // Update session accumulators
  if (eventData.sample_id)      session.materialsViewed.add(eventData.sample_id);
  if (eventData.application)    session.viewsVisited.add(eventData.application);
  if (eventData.category)       session.brandsExplored.add(eventData.category);
  if (eventData.color)          session.cabinetColorsTried.add(eventData.color);
  if (eventName === 'visual_liked')           session.likesCount++;
  if (eventName === 'visual_downloaded')      session.downloadsCount++;
  if (eventName === 'color_comparison_logged') session.comparisonsCount++;
  if (eventName === 'checkout_start' || eventName === 'sample_request_submitted')
    session.sampleRequested = true;

  posthog.capture(eventName, payload);
  await send(getEndpoint(eventName), payload);
};

// ─── Session summary (fire on page hide / tab close) ─────────────────────────
export const trackSessionSummary = async (exitTrigger = 'pagehide') => {
  const duration = Math.round((Date.now() - session.startTime) / 1000);

  // Skip very short sessions (< 5s) — bots / accidental loads
  if (duration < 5) return;

  const payload = {
    event:                'session_summary',
    timestamp:            new Date().toISOString(),
    session_id:           getSessionId(),
    device:               getDeviceType(),
    url:                  window.location.href,
    duration_seconds:     duration,
    materials_viewed:     session.materialsViewed.size,
    likes_count:          session.likesCount,
    downloads_count:      session.downloadsCount,
    comparisons_count:    session.comparisonsCount,
    sample_requested:     session.sampleRequested,
    views_visited:        [...session.viewsVisited],
    cabinet_colors_tried: [...session.cabinetColorsTried],
    brands_explored:      [...session.brandsExplored],
    exit_trigger:         exitTrigger,
  };

  await send(ENDPOINTS.session_summary, payload);
};
